/**
 * Meta (Facebook) Pixel E-Commerce Event Tracking
 *
 * Fires standard Meta Pixel events for retargeting and conversion tracking.
 * All functions are safe to call even if fbq is not available.
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

function isFbqAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/** Track a product page view */
export function fbTrackViewContent(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'ViewContent', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_category: product.category || '',
    content_type: 'product',
    value: product.price || 0,
    currency: 'USD',
  });
}

/** Track add to cart */
export function fbTrackAddToCart(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
  quantity?: number;
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'AddToCart', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_type: 'product',
    value: (product.price || 0) * (product.quantity || 1),
    currency: 'USD',
  });
}

/** Track checkout initiation */
export function fbTrackInitiateCheckout(params: {
  value: number;
  numItems: number;
  contentIds?: string[];
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'InitiateCheckout', {
    value: params.value,
    currency: 'USD',
    num_items: params.numItems,
    content_ids: params.contentIds || [],
    content_type: 'product',
  });
}

/** Track completed purchase */
export function fbTrackPurchase(params: {
  value: number;
  orderId?: string;
  contentIds?: string[];
  numItems?: number;
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Purchase', {
    value: params.value,
    currency: 'USD',
    content_ids: params.contentIds || [],
    content_type: 'product',
    num_items: params.numItems || 1,
  });
}

/** Track search */
export function fbTrackSearch(query: string) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Search', {
    search_string: query,
  });
}

/** Track lead (e.g., email signup, sample request) */
export function fbTrackLead(params?: { value?: number; category?: string }) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Lead', {
    value: params?.value || 0,
    currency: 'USD',
    content_category: params?.category || '',
  });
}

/** Track custom event */
export function fbTrackCustom(eventName: string, params?: Record<string, any>) {
  if (!isFbqAvailable()) return;
  window.fbq!('trackCustom', eventName, params || {});
}
